<script setup lang="ts">
/**
 * The agent asking to lift the chat's document scope (§6b).
 *
 * Scope is strict: the agent can't widen it, so this card is the only route from "what you're asking
 * for is outside this case" to actually reaching it. It's phrased as a request, and the scope name
 * comes from the store rather than the model — the user is agreeing to a specific, real change.
 */
import { Globe, X } from 'lucide-vue-next'
import type { ClearScopeRequest } from '~/types/web-chat'

defineProps<{ request: ClearScopeRequest }>()

const emit = defineEmits<{
  'confirm-clear-scope': []
  'cancel-clear-scope': []
}>()

const { t } = useI18n()
</script>

<template>
  <div class="mt-3 rounded-lg border border-border bg-muted/40 p-3">
    <div class="flex items-start gap-2">
      <div class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-lighter">
        <Globe class="h-3 w-3 text-primary" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium text-foreground">{{ t('clients.hub.clearScopeTitle') }}</p>
        <!-- i18n-t so the scope name can be emphasised inside the sentence without splitting the
             sentence into fragments translators cannot reorder. -->
        <i18n-t keypath="clients.hub.clearScopeBody" tag="p" class="mt-0.5 text-xs leading-relaxed text-muted-foreground" scope="global">
          <template #scope><span class="font-medium text-foreground">{{ request.scopeLabel }}</span></template>
        </i18n-t>
        <p v-if="request.reason" class="mt-1.5 text-xs italic leading-relaxed text-muted-foreground">
          “{{ request.reason }}”
        </p>

        <div v-if="request.status === 'pending'" class="mt-2 flex flex-wrap gap-1.5">
          <Button type="button" size="sm" class="gap-1.5" @click="emit('confirm-clear-scope')">
            <Globe class="h-3 w-3" />
            {{ t('clients.hub.clearScopeConfirm') }}
          </Button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent-lighter hover:text-foreground"
            @click="emit('cancel-clear-scope')"
          >
            <X class="h-3 w-3" />
            {{ t('clients.hub.clearScopeCancel') }}
          </button>
        </div>
        <p v-else class="mt-2 text-[11px] text-muted-foreground">
          {{ request.status === 'confirmed'
            ? t('clients.hub.clearScopeConfirmed')
            : t('clients.hub.clearScopeCancelled') }}
        </p>
      </div>
    </div>
  </div>
</template>
